import {Box, ToggleButton, ToggleButtonGroup} from "@mui/material";
import * as React from "react";
import {useTranslation} from "react-i18next";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import {InsightsLoading} from "./insightsLoading";
import {CHART_HEIGHT} from "./insightsChart";
import {InsightsCard} from "./InsightsCard";

type SeriesView = "series" | "list";

interface Props {
    title: string;
    hint?: string;
    headline?: React.ReactNode;
    actions?: React.ReactNode;
    toolbar?: React.ReactNode;
    chart: React.ReactNode;
    table: React.ReactNode;
    legend?: React.ReactNode;
    empty?: React.ReactNode;
    loading?: InsightsLoading;
    /**
     * The floor under the chart area. A plot needs `CHART_HEIGHT` to be readable, but a card whose
     * chart is a single bar would otherwise sit in 300px of white — so it says its own height.
     */
    chartMinHeight?: number;
}

/** Container for series cards with toggle between chart and table views. */
export function SeriesCard({
    title,
    hint,
    headline,
    actions,
    toolbar,
    chart,
    table,
    legend,
    empty,
    loading,
    chartMinHeight = CHART_HEIGHT,
}: Props) {
    const {t} = useTranslation();

    const [view, setView] = React.useState<SeriesView>("series");

    const isEmpty = empty != null;

    const toggle = (
        <ToggleButtonGroup
            exclusive
            size={"small"}
            value={view}
            onChange={(_, next: SeriesView | null) => next && setView(next)}
        >
            <ToggleButton value={"series"} aria-label={t("Chart")}>
                <ShowChartRoundedIcon fontSize={"small"}/>
            </ToggleButton>

            <ToggleButton value={"list"} aria-label={t("List")}>
                <FormatListBulletedRoundedIcon fontSize={"small"}/>
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <InsightsCard
            title={title}
            hint={hint}
            headline={headline}
            loading={loading}
            actions={(
                <React.Fragment>
                    {actions}
                    {toggle}
                </React.Fragment>
            )}
            footer={!isEmpty && view === "series" ? legend : undefined}
        >
            {toolbar}

            <Box sx={{minWidth: 0, minHeight: chartMinHeight, ...(isEmpty && {display: "grid", placeItems: "center"})}}>
                {isEmpty ? empty : view === "list" ? table : chart}
            </Box>
        </InsightsCard>
    );
}
